import { createAsyncThunk } from '@/redux/createAsyncThunk';
import { getAuthToken, isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { getDeployment } from '@/redux/modules/config';
import { getFollowedSellerIds } from '@/redux/modules/followSeller';
import { getHomeData } from '@/redux/modules/home/home.api';
import { shouldFetchHomeData } from '@/redux/modules/home/home.selectors';
import { MAX_TODAY_AUCTIONS } from '@/redux/modules/home/home.types';
import { fetchItemSummariesIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import { fetchRecommendedItemIfNeeded, getSortedRecommendedItemIdsFromState } from '@/redux/modules/recommendedItem';
import {
    fetchSavedSearchesIfNeeded,
    fetchSavedSearchItemsIfNeeded,
    getSavedSearchItemIds,
    hasSavedSearchesSelector,
} from '@/redux/modules/savedSearch';
import { fetchSellerUpcomingCatalogsIfNeeded } from '@/redux/modules/sellerUpcomingCatalogs';
import { fetchUpcomingItemsIfNeeded } from '@/redux/modules/upcoming/items/upcomingItems.actions';
import { getUpcomingItemIds } from '@/redux/modules/upcoming/items/upcomingItems.selectors';
import { fetchUpcomingCatalogsIfNeeded } from '@/redux/modules/upcomingCatalogs';
import { HomePageData } from '@/types/Home';

/**
 * @deprecated If using redux-toolkit's createSlice, use fetchHomeData.fulfilled
 */
export const LOAD_HOME_DATA_SUCCESS = 'la/domain/home/LOAD_SUCCESS';

type HomeDataRequest = {
    forceUpdate?: boolean;
    shouldUseAverageLotValueActionCalendarRanking?: boolean;
};

export const fetchHomeData = createAsyncThunk<HomePageData, HomeDataRequest>(
    'la/ui/home/fetchHomeData',
    async ({ forceUpdate = false, shouldUseAverageLotValueActionCalendarRanking }, { dispatch, getState }) => {
        const state = getState();
        const deployment = getDeployment(state);
        const authToken = getAuthToken(state);

        const response = await getHomeData({
            authToken,
            deployment,
            forceUpdate,
            shouldUseAverageLotValueActionCalendarRanking,
        });
        dispatch({
            meta: { actionTime: Date.now() },
            payload: response.data,
            type: LOAD_HOME_DATA_SUCCESS,
        });
        return response.data;
    },
    {
        condition: ({ forceUpdate = false }, { getState }) => forceUpdate || shouldFetchHomeData(getState()),
    }
);

export const fetchAllHomeData = createAsyncThunk<void, void>(
    'la/ui/home/fetchAllHomeData',
    async (_, { dispatch, getState }) => {
        const state = getState();
        const hasSavedSearches = hasSavedSearchesSelector(state);
        const followedSellerIds = getFollowedSellerIds(state);
        await Promise.all([
            dispatch(fetchHomeData({ forceUpdate: false })),
            dispatch(fetchUpcomingItemsIfNeeded()),
            dispatch(fetchUpcomingCatalogsIfNeeded(MAX_TODAY_AUCTIONS, true)),
            dispatch(fetchSavedSearchesIfNeeded()),
            dispatch(fetchSavedSearchItemsIfNeeded(hasSavedSearches)),
            dispatch(fetchRecommendedItemIfNeeded(false)),
            ...followedSellerIds.map((sellerId) => dispatch(fetchSellerUpcomingCatalogsIfNeeded(sellerId))),
        ]);
        const newState = getState();
        const isLoggedIn = isUserLoggedIn(newState);

        if (isLoggedIn) {
            const upcomingItemIds = getUpcomingItemIds(newState);
            const savedSearchItemIds = getSavedSearchItemIds(newState);
            const recommendedItemIds = getSortedRecommendedItemIdsFromState(newState, 12);
            const promises: Promise<any>[] = [];
            promises.push(
                dispatch(
                    fetchItemSummariesIfNeeded({
                        identifier: 'COMING-UP-Carousel',
                        itemIds: upcomingItemIds,
                    })
                )
            );
            promises.push(
                dispatch(
                    fetchItemSummariesIfNeeded({
                        identifier: 'FOLLOWED-SEARCH-Carousel',
                        itemIds: savedSearchItemIds,
                    })
                )
            );
            promises.push(
                dispatch(
                    fetchItemSummariesIfNeeded({
                        identifier: 'RECOMMENDED-ITEM-Carousel',
                        itemIds: recommendedItemIds,
                    })
                )
            );
            await Promise.all(promises);
        }
    }
);
